import { Button } from "../../../../components/common/button";
import { CheckoutStepper } from "./checkout-stepper";
import { DeliveryAddressSection } from "./delivery-address-section";
import { BasketOrderSummary } from "./basket-order-summary";
import { CompanyAddressNotice } from "./company-address-notice";
import { useCheckoutStore } from "../../domain/useCheckoutStore";
import { useMyAddresses } from "../../../addresses/hooks/useMyAddresses";
import { useCompanyAddressGaps } from "../../hooks/useCompanyAddressGaps";
import { showErrorToast } from "../../../../utils/toasts";
import { Heading, Text } from "../../../../components/common/typography";

interface DeliveryViewProps {
  onProceed: () => void;
}

export function DeliveryView({ onProceed }: DeliveryViewProps) {
  const shippingAddressId = useCheckoutStore((s) => s.shippingAddressId);
  const { data: deliveryAddresses } = useMyAddresses("delivery");
  const { missing: companyAddressGaps } = useCompanyAddressGaps();

  // The store can briefly hold an id from an earlier visit before the
  // address book has loaded and reconciled it, so only an id that is in the
  // buyer's current book counts as chosen.
  const hasDeliveryAddress =
    shippingAddressId != null &&
    !!deliveryAddresses?.some((address) => address.id === shippingAddressId);
  const canProceed = hasDeliveryAddress && companyAddressGaps.length === 0;

  const handleProceed = () => {
    if (!hasDeliveryAddress) {
      showErrorToast("Please select or add a delivery address first.");
      return;
    }
    if (companyAddressGaps.length > 0) {
      showErrorToast("Please complete your company address first.");
      return;
    }
    onProceed();
  };

  return (
    <div className="flex w-full flex-col gap-6 px-8 py-6">
      <CheckoutStepper current={1} />

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          <div className="rounded-2xl border border-borderDefault bg-bgWhite p-5">
            <Heading variant="h3" tone="dark" className="mb-1">
              Delivery included
            </Heading>
            <Text variant="body" tone="body">
              Delivery is included in the product price. Your order may arrive in multiple parcels, all visible at the order level after checkout.
            </Text>
          </div>
          {companyAddressGaps.length > 0 && (
            <CompanyAddressNotice missing={companyAddressGaps} />
          )}
          <DeliveryAddressSection />
          <Button
            variant="primary"
            fullWidth
            onClick={handleProceed}
            disabled={!canProceed}
          >
            Continue to Payment
          </Button>
        </div>

        <div className="lg:col-span-1">
          <BasketOrderSummary
            onProceed={handleProceed}
            buttonLabel="Continue to Payment"
            proceedDisabled={!canProceed}
          />
        </div>
      </div>
    </div>
  );
}
